import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	Box,
	Button,
	FormControl,
	FormLabel,
	InputLabel,
	MenuItem,
	Select,
	Stack,
} from "@mui/material";
import React, {
	useCallback,
	useContext,
	useEffect,
	useRef,
	useState,
} from "react";
import { useTranslation } from "react-i18next";
import TPDropDown from "./TPDropDown";
import TPCheckbox from "./TPCheckbox";
import TradingPartnerData from "./TradingPartnerData";
import { useDispatch, useSelector } from "react-redux";
import { treeActions } from "../../redux/slices/treeSlice";
import { ParentScreenCtx } from "/src/pages/workbench_build/WorkbenchBuild";
import { cloneDeep, debounce, isEqual } from "lodash";
import { eachDeep } from "deepdash-es/standalone";
import { SectionHeader } from "../ItemProperties/Forms/SectionHeader";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

const OutboundTemplatePropertiesFields = ({ datatype }) => {
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const parentScreen = useContext(ParentScreenCtx);
	const [sendRecord, setSendRecord] = useState("");
	const [executeSQL, setExecuteSQL] = useState(false);
	const [inboundWorkflow, setInboundWorkflow] = useState([]);
	const [inboundTemplate, setInboundTemplate] = useState([]);
	const [workflowOptions, setWorkflowOptions] = useState([]);
	const [templateOptions, setTemplateOptions] = useState([]);
	const [allowDynamicFilename, setAllowDynamicFilename] = useState(false);
	const [quoteData, setQuoteData] = useState(false);
	const [trimData, setTrimData] = useState(false);
	const [urlEncode, setUrlEncode] = useState(false);
	const [escapeData, setEscapeData] = useState(false);
	const [includeNull, setIncludeNull] = useState(false);
	const [sendDataEmpty, setSendDataEmpty] = useState(false);
	const [keepOrignalData, setKeepOrignalData] = useState(false);
	const [removeTrailingField, setRemoveTrailingField] = useState(false);
	const [incrementRelation, setIncrementRelation] = useState(false);
	const [suppressAttributes, setSuppressAttributes] = useState(false);
	const [suppressEmptyElements, setSUppressEmptyElements] = useState(false);
	const [fillCharacterValidation, setFillCharacterValidation] = useState(false);
	const rightTree = useSelector(
		(state) => state.treeData[parentScreen].rightTree
	);
	const tDataAllTabs = rightTree.tree;
	const translatedData = rightTree.tree[rightTree.currentTab].translatedData;
	const selectedNode = rightTree.tree[rightTree.currentTab].selectedNode;
	const templateHeader = rightTree.tree.templateHeader;
	const initialTemplateHeader = rightTree.tree.initialTemplateHeader;

	const sendRecordsOption = [
		{ title: t("LBL_ALL_AT_ONCE"), value: false },
		{ title: t("LBL_ONE_AT_A_TIME"), value: true },
	].map((obj, key) => (
		<MenuItem key={key} value={obj.value}>
			{obj.title}
		</MenuItem>
	));

	const executeSQLOption = [
		{ title: t("LBL_EXECUTE_BATCH"), value: false },
		{ title: t("LBL_EXECUTE_IMMEDIATE"), value: true },
	].map((obj, key) => (
		<MenuItem key={key} value={obj.value}>
			{obj.title}
		</MenuItem>
	));

	const saveTemplateHeaders = (key, value) => {
		dispatch(
			treeActions.setRightTreeTemplateHeaders({
				screen: parentScreen,
				key,
				value,
			})
		);
		
	};

	const debouncedSave = useCallback(
		debounce(
			(prop, newVal, tData, currTab) =>
				changePropOnAllFields(prop, newVal, tData, currTab, dispatch,parentScreen),
			200,
			true
		),
		[]
	);

	const handleIncludeNullAll = (checked) => {
		debouncedSave("INCLUDE_NULL", checked, tDataAllTabs, rightTree.currentTab);
		saveTemplateHeaders("INCLUDE_NULL_ALL", checked);
	};

	const handleQuoteAll = (checked) => {
		debouncedSave("QUOTE_DATA", checked, tDataAllTabs, rightTree.currentTab);
		saveTemplateHeaders("QUOTE_DATA", checked);
	};

	const handleTrimAll = (checked) => {
		debouncedSave("TRIMDATA", checked, tDataAllTabs, rightTree.currentTab);
		saveTemplateHeaders("TRIMDATA", checked);
	};

	const handleUrlEncodeAll = (checked) => {
		debouncedSave("URLENCODE", checked, tDataAllTabs, rightTree.currentTab);
		saveTemplateHeaders("URLENCODE", checked);
	};

	const handleEscapeAll = (checked) => {
		debouncedSave("ESCAPE_DATA", checked, tDataAllTabs, rightTree.currentTab);
		saveTemplateHeaders("ESCAPE_DATA", checked);
	};

	const handleSuppressAttribute = (checked) => {
		debouncedSave("SUPP_ATTRIB_MSSNG_FIELD_IDENTIFIER", checked, tDataAllTabs, rightTree.currentTab);
		saveTemplateHeaders("SUPP_ATTRIB_MSSNG_FIELD_IDENTIFIER", checked);
	};

	const handleSuppressEmptyElements = (checked) => {
		debouncedSave("SUPPRESS_EMPTY_RECORDS", checked, tDataAllTabs, rightTree.currentTab);
		saveTemplateHeaders("SUPPRESS_EMPTY_RECORDS", checked);
	};
	

	const valueToBoolean = (val) => {
		if (val === undefined) return false;
		return val
			? (typeof val === "boolean" && val) || val === "TRUE" || false
			: false;
	};

	useEffect(() => {
		const dependency = templateHeader?.DEPENDENCIES?.split("/") || [];
		setSendRecord(valueToBoolean(templateHeader.SEND_EACH_RELATION));
		setExecuteSQL(
			valueToBoolean(templateHeader?.PROCESS_RECORD_REAL_TIME) || false
		);
		setAllowDynamicFilename(
			valueToBoolean(templateHeader?.ALLOW_DYNAMIC_FILENAME)
		);
		setQuoteData(valueToBoolean(templateHeader?.QUOTE_DATA));
		setTrimData(valueToBoolean(templateHeader?.TRIMDATA));
		setUrlEncode(valueToBoolean(templateHeader?.URLENCODE));
		setEscapeData(valueToBoolean(templateHeader?.ESCAPE_DATA));
		setKeepOrignalData(valueToBoolean(templateHeader?.KEEP_ORIG_DATA_ON_ERROR));
		setSendDataEmpty(valueToBoolean(templateHeader?.SEND_DATA_IF_EMPTY_BODY));
		setRemoveTrailingField(
			valueToBoolean(templateHeader?.REMOVE_TRAILING_FIELD_DELIMITERS)
		);
		setIncrementRelation(
			templateHeader?.INCREMENT_RELATION_COUNT === "FALSE" ? true : templateHeader?.INCREMENT_RELATION_COUNT === false ? true : false
		);
		setFillCharacterValidation(
			valueToBoolean(templateHeader?.FILL_CHARACTER_VALIDATION)
		);
		setIncludeNull(valueToBoolean(templateHeader?.INCLUDE_NULL_ALL));
		setSuppressAttributes(valueToBoolean(templateHeader?.SUPP_ATTRIB_MSSNG_FIELD_IDENTIFIER));
		setSUppressEmptyElements(valueToBoolean(templateHeader?.SUPPRESS_EMPTY_RECORDS));
	}, [initialTemplateHeader]);

	return (
		<>
			<Accordion defaultExpanded sx={styles.accordianContainer}>
				<AccordionSummary
					expandIcon={<ExpandMoreIcon />}
					aria-controls='accordianFormattingOptions'
					id='accordianFormattingOptions'
					className='accordian-override'
					sx={styles.accordianHeader}
				>
					<SectionHeader text={t("LBL_PROCESSING_OPTIONS")} />
				</AccordionSummary>
				<AccordionDetails sx={styles.accordianContent}>
					<Stack spacing={"10px"}>
						{datatype !== "DATABASE" && (
							<TPDropDown
								label={t("LBL_SND_RECORDS")}
								options={sendRecordsOption}
								value={sendRecord || false}
								onChange={(e) => {
									const value = e.target.value;
									setSendRecord(value || "");
									saveTemplateHeaders("SEND_EACH_RELATION", value);
									if (value) {
										saveTemplateHeaders("ALLOW_DYNAMIC_FILENAME", "FALSE");
										setAllowDynamicFilename(false);
									}
								}}
							/>
						)}
						{datatype === "DATABASE" && (
							<TPDropDown
								label={t("LBL_EXECUTE_SQL")}
								options={executeSQLOption}
								value={executeSQL}
								onChange={(e) => {
									const value = e.target.value;
									setExecuteSQL(value || false);
									saveTemplateHeaders("PROCESS_RECORD_REAL_TIME", value);
								}}
							/>
						)}
						<div className='flex flex-wrap'>
							{datatype !== "DATABASE" && !sendRecord && (
								<TPCheckbox
									disabled={sendRecord === true}
									label={t("BTN_ALLOW_DYNAMIC_FILENAME")}
									checked={allowDynamicFilename}
									handleCheck={(checked) => {
										setAllowDynamicFilename(checked);
										saveTemplateHeaders("ALLOW_DYNAMIC_FILENAME", checked);
									}}
								/>
							)}
							<TPCheckbox
								label={t("BTN_SND_DATA_IF_BODY_EMPTY")}
								checked={sendDataEmpty}
								handleCheck={(checked) => {
									setSendDataEmpty(checked);
									saveTemplateHeaders("SEND_DATA_IF_EMPTY_BODY", checked);
								}}
							/>
							<TPCheckbox
								label={t("BTN_DONT_INC_INT_RELATION_CNT")}
								checked={incrementRelation}
								handleCheck={(checked) => {
									setIncrementRelation(checked);
									saveTemplateHeaders("INCREMENT_RELATION_COUNT", !checked);
								}}
							/>
						</div>
					</Stack>
				</AccordionDetails>
			</Accordion>

			<Accordion defaultExpanded sx={styles.accordianContainer}>
				<AccordionSummary
					expandIcon={<ExpandMoreIcon />}
					aria-controls='accordianFormattingOptions'
					id='accordianFormattingOptions'
					className='accordian-override'
					sx={styles.accordianHeader}
				>
					<SectionHeader text={t("LBL_FORMATTING_OPTIONS")} />
				</AccordionSummary>
				<AccordionDetails sx={styles.accordianContent}>
					<Stack spacing={"10px"}>
						<div className='flex flex-wrap'>
							{datatype === "JSON" && (
								<TPCheckbox
									label={t("BTN_INCLUDE_IF_NULL_ALL_FLDS")}
									checked={includeNull}
									handleCheck={(checked) => {
										setIncludeNull(checked);
										handleIncludeNullAll(checked);
									}}
								/>
							)}
							<TPCheckbox
								label={t("BTN_QUOTE_ALL_FLDS")}
								checked={quoteData}
								handleCheck={(checked) => {
									setQuoteData(checked);
									handleQuoteAll(checked);
								}}
							/>
							{datatype !== "FIXED_LENGTH" && (
								<TPCheckbox
									label={t("BTN_TRIM_ALL_FLDS")}
									checked={trimData}
									handleCheck={(checked) => {
										setTrimData(checked);
										handleTrimAll(checked);
									}}
								/>
							)}
							<TPCheckbox
								label={t("BTN_URLENCODE_ALL_FLDS")}
								checked={urlEncode}
								handleCheck={(checked) => {
									setUrlEncode(checked);
									handleUrlEncodeAll(checked);
								}}
							/>
							{datatype === "JSON" && (
								<TPCheckbox
									label={t("BTN_ESCAPE_ALL_FIELDS")}
									checked={escapeData}
									handleCheck={(checked) => {
										setEscapeData(checked);
										handleEscapeAll(checked);
									}}
								/>
							)}
							<TPCheckbox
								label={t("BTN_KEEP_ORIG_DATA_ON_CONV_ERR")}
								checked={keepOrignalData}
								handleCheck={(checked) => {
									setKeepOrignalData(checked);
									saveTemplateHeaders("KEEP_ORIG_DATA_ON_ERROR", checked);
								}}
							/>

							{!["DATABASE"].includes(datatype) && (
								<TPCheckbox
									label={t("BTN_REM_TRAILING_EMPTY_FLD_DELIM")}
									checked={removeTrailingField}
									handleCheck={(checked) => {
										setRemoveTrailingField(checked);
										saveTemplateHeaders(
											"REMOVE_TRAILING_FIELD_DELIMITERS",
											checked
										);
									}}
								/>
							)}

							{datatype === "FIXED_LENGTH" && (
								<TPCheckbox
									label={t("BTN_FILL_CHAR_VALIDATION")}
									checked={fillCharacterValidation}
									handleCheck={(checked) => {
										setFillCharacterValidation(checked);
										saveTemplateHeaders(
											"FILL_CHARACTER_VALIDATION",
											checked
										);
									}}
								/>
							)}

							{datatype === "XML" && (
								<TPCheckbox
									label={t("BTN_SUPP_ATTRIB_MSSNG_FLD_IDENT")}
									checked={suppressAttributes}
									handleCheck={(checked) => {
										setSuppressAttributes(checked);
										saveTemplateHeaders(
											"SUPP_ATTRIB_MSSNG_FIELD_IDENTIFIER",
											checked
										);
										handleSuppressAttribute(checked)
									}}
								/>
							)}

							{datatype === "XML" && (
								<TPCheckbox
									label={t("BTN_SUPPRESS_EMPTY_RECORDS")}
									checked={suppressEmptyElements}
									handleCheck={(checked) => {
										setSUppressEmptyElements(checked);
										handleSuppressEmptyElements(checked)
									}}
								/>
							)}
						</div>
					</Stack>
				</AccordionDetails>
			</Accordion>
		</>
	);
};
//loop through all template nodes for header/body/trailer, change all fields with prop to newVal, or delete prop if newVal is false.
export const changePropOnAllFields = (prop, newVal, tData, currTab,dispatch,parentScreen) => {
	let snHeader = cloneDeep(tData.header.selectedNode);
	let snBody = cloneDeep(tData.body.selectedNode);
	let snTrailer = cloneDeep(tData.trailer.selectedNode);

	let updatedHeaderData = eachDeep(cloneDeep(tData.header.translatedData), (value, key) => {
		if (typeof value !== "object") return value;
		if (prop === "SUPP_ATTRIB_MSSNG_FIELD_IDENTIFIER" && ["ATTRIBUTE"].includes(value?.ICON_DISPLAY)) {
			if (newVal) value[prop] = "TRUE";
			else if (value[prop] !== undefined) delete value[prop];
		} else if (prop === "SUPPRESS_EMPTY_RECORDS" && ["ELEMENT", "REPEATING_ELEMENT"].includes(value?.ICON_DISPLAY)) {
			if (newVal) value[prop] = "TRUE";
			else if (value[prop] !== undefined) delete value[prop];
		} else if (
			[
				"DATABASE_FIELD",
				"DATABASE_FIELD_KEY",
				"DATABASE_FIELD_VALID",
				"DATABASE_FIELD_VALID_KEY",
				"FIELD",
			].includes(value?.ICON_DISPLAY)
		) {
			if (newVal) value[prop] = "TRUE";
			else if (value[prop] !== undefined) delete value[prop];
		}
		if (Object.keys(snHeader).length > 0 && value?.key === snHeader?.key) snHeader = value;
	});
	let updatedBodyData = eachDeep(cloneDeep(tData.body.translatedData), (value, key) => {
		if (typeof value !== "object") return value;
		
		if (prop === "SUPP_ATTRIB_MSSNG_FIELD_IDENTIFIER" && ["ATTRIBUTE"].includes(value?.ICON_DISPLAY)) {
			if (newVal) value[prop] = "TRUE";
			else if (value[prop] !== undefined) delete value[prop];
		} else if (prop === "SUPPRESS_EMPTY_RECORDS" && ["ELEMENT", "REPEATING_ELEMENT"].includes(value?.ICON_DISPLAY)) {
			if (newVal) value[prop] = "TRUE";
			else if (value[prop] !== undefined) delete value[prop];
		} else if (
			[
				"DATABASE_FIELD",
				"DATABASE_FIELD_KEY",
				"DATABASE_FIELD_VALID",
				"DATABASE_FIELD_VALID_KEY",
				"FIELD",
			].includes(value?.ICON_DISPLAY)
		) {
			
			if (newVal) value[prop] = "TRUE";
			else if (value[prop] !== undefined) delete value[prop];
		}
		if (Object.keys(snBody).length > 0 && value?.key === snBody?.key) snBody = value;
		return value;
	});
	let updatedTrailerData = eachDeep(cloneDeep(tData.trailer.translatedData), (value, key) => {
		if (typeof value !== "object") return value;
		if (prop === "SUPP_ATTRIB_MSSNG_FIELD_IDENTIFIER" && ["ATTRIBUTE"].includes(value?.ICON_DISPLAY)) {
			if (newVal) value[prop] = "TRUE";
			else if (value[prop] !== undefined) delete value[prop];
		} else if (prop === "SUPPRESS_EMPTY_RECORDS" && ["ELEMENT", "REPEATING_ELEMENT"].includes(value?.ICON_DISPLAY)) {
			if (newVal) value[prop] = "TRUE";
			else if (value[prop] !== undefined) delete value[prop];
		} else if (
			[
				"DATABASE_FIELD",
				"DATABASE_FIELD_KEY",
				"DATABASE_FIELD_VALID",
				"DATABASE_FIELD_VALID_KEY",
				"FIELD",
			].includes(value?.ICON_DISPLAY)
		) {
			if (newVal) value[prop] = "TRUE";
			else if (value[prop] !== undefined) delete value[prop];
		}
		if (Object.keys(snTrailer).length > 0 && value?.key === snTrailer?.key) snTrailer = value;
	});

	[{oData: tData.header.translatedData, uData: updatedHeaderData, tab: 'header'}, {oData: tData.body.translatedData, uData: updatedBodyData, tab: 'body'}, {oData: tData.trailer.translatedData, uData: updatedTrailerData, tab: 'trailer'}].forEach((data) => {
		
		if (!isEqual(data.oData, data.uData)) {
			dispatch(
				treeActions.setNewRightTreeTranslatedData({
					screen: parentScreen,
					type: data.tab,
					data: data.uData,
				})
			);
			let selNodeTab = data.tab === 'header' ? snHeader : data.tab === 'body' ? snBody : snTrailer;
			dispatch(
				treeActions.setRightTreeSelectedNode({
					screen: parentScreen,
					type: data.tab,
					data: { ...selNodeTab, avoidSelection: true },
				})
			);
			dispatch(
	  treeActions.setRightTreeCurrentTab({
		screen: parentScreen,
		data: currTab
	  })
	);
		}
	});
};
export default OutboundTemplatePropertiesFields;

const styles = {
	fpContainer: {
		// padding: "0 16px 16px 16px"
		padding: "0 4px 4px 4px",
	},
	accordianContainer: {
		border: "none",
		padding: "0",
		margin: "0",
		boxShadow: "none",
		backgroundColor: "unset",
	},
	accordianHeader: {
		border: "none",
		padding: "0",
		margin: "0",
		backgroundColor: "unset",
	},
	accordianContent: {
		border: "none",
		padding: "0 0px",
		margin: "0",
		backgroundColor: "unset",
	},
};
